import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2, Ticket, Megaphone } from 'lucide-react';
import api from '@/services/apiClient';
import PageHeader from '@/components/ui/PageHeader';
import DataTable from '@/components/ui/DataTable';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Modal from '@/components/ui/Modal';
import EmptyState from '@/components/ui/EmptyState';
import CouponForm from './CouponForm';
import CampaignForm from './CampaignForm';
import { COUPON_TYPE_LABELS, fmtNum, fmtDate } from '@/lib/enums';
import styles from './Coupons.module.css';

// Rótulo do valor do cupom conforme o tipo.
const couponValueLabel = (c) =>
  c.type === 'percent' ? `${fmtNum(c.value)}%` : `R$ ${fmtNum(c.value)}`;

export default function AdminCoupons() {
  const { t } = useTranslation();
  const [tab, setTab] = useState('coupons'); // 'coupons' | 'campaigns'
  const [coupons, setCoupons] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {} novo | obj | null

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/coupons').then((r) => (r.data || r).coupons || []).catch(() => []),
      api.get('/campaigns').then((r) => (r.data || r).campaigns || []).catch(() => []),
    ])
      .then(([cps, cmp]) => {
        setCoupons(cps);
        setCampaigns(cmp);
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  // --- Salvar / excluir (cupons) -------------------------------------------
  const saveCoupon = async (form) => {
    if (editing?.id) await api.put(`/coupons/${editing.id}`, form);
    else await api.post('/coupons', form);
    setEditing(null);
    load();
  };
  const removeCoupon = async (id) => {
    if (!confirm(t('coupons.confirmDeleteCoupon'))) return;
    try {
      await api.delete(`/coupons/${id}`);
      load();
    } catch (e) {
      alert(e.message);
    }
  };

  // --- Salvar / excluir (campanhas) ----------------------------------------
  const saveCampaign = async (form) => {
    if (editing?.id) await api.put(`/campaigns/${editing.id}`, form);
    else await api.post('/campaigns', form);
    setEditing(null);
    load();
  };
  const removeCampaign = async (id) => {
    if (!confirm(t('campaigns.confirmDeleteCampaign'))) return;
    try {
      await api.delete(`/campaigns/${id}`);
      load();
    } catch (e) {
      alert(e.message);
    }
  };

  const couponColumns = [
    {
      key: 'code',
      header: t('coupons.colCode'),
      render: (r) => <span style={{ fontWeight: 600, fontFamily: 'var(--font-mono, monospace)' }}>{r.code}</span>,
    },
    { key: 'type', header: t('coupons.colType'), render: (r) => COUPON_TYPE_LABELS[r.type] || r.type },
    { key: 'value', header: t('coupons.colDiscount'), render: couponValueLabel },
    {
      key: 'owner',
      header: t('coupons.colOrigin'),
      render: (r) =>
        r.trainerId ? <Badge>{r.trainer?.name || t('coupons.trainer')}</Badge> : <Badge variant="accent">{t('coupons.platform')}</Badge>,
    },
    {
      key: 'redemptions',
      header: t('coupons.colUses'),
      render: (r) => `${fmtNum(r.redemptions)}${r.maxRedemptions ? ` / ${fmtNum(r.maxRedemptions)}` : ''}`,
    },
    { key: 'expiresAt', header: t('coupons.colExpires'), render: (r) => fmtDate(r.expiresAt) },
    {
      key: 'active',
      header: t('screens.status'),
      render: (r) =>
        r.active ? <Badge variant="success">{t('coupons.statusActive')}</Badge> : <Badge variant="neutral">{t('coupons.statusInactive')}</Badge>,
    },
    {
      key: 'actions',
      header: t('screens.actions'),
      align: 'right',
      render: (r) => (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button size="sm" variant="ghost" icon={Pencil} onClick={() => setEditing(r)}>{t('common.edit')}</Button>
          <Button size="sm" variant="ghost" icon={Trash2} onClick={() => removeCoupon(r.id)} />
        </div>
      ),
    },
  ];

  const campaignColumns = [
    { key: 'name', header: t('campaigns.colCampaign'), render: (r) => <span style={{ fontWeight: 600 }}>{r.name}</span> },
    { key: 'discountPct', header: t('campaigns.colDiscount'), render: (r) => (r.discountPct ? `${fmtNum(r.discountPct)}%` : '—') },
    { key: 'period', header: t('campaigns.colPeriod'), render: (r) => `${fmtDate(r.startsAt)} → ${fmtDate(r.endsAt)}` },
    {
      key: 'active',
      header: t('screens.status'),
      render: (r) =>
        r.active ? <Badge variant="success">{t('campaigns.statusActive')}</Badge> : <Badge variant="neutral">{t('campaigns.statusInactive')}</Badge>,
    },
    {
      key: 'actions',
      header: t('screens.actions'),
      align: 'right',
      render: (r) => (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button size="sm" variant="ghost" icon={Pencil} onClick={() => setEditing(r)}>{t('common.edit')}</Button>
          <Button size="sm" variant="ghost" icon={Trash2} onClick={() => removeCampaign(r.id)} />
        </div>
      ),
    },
  ];

  const isCoupons = tab === 'coupons';

  return (
    <>
      <PageHeader kicker="Movez · Admin" title={t('nav.coupons')}>
        <Button icon={Plus} onClick={() => setEditing({})}>
          {isCoupons ? t('coupons.newCoupon') : t('campaigns.newCampaign')}
        </Button>
      </PageHeader>

      <div className={styles.tabs}>
        <button type="button" className={isCoupons ? styles.tabActive : styles.tab} onClick={() => setTab('coupons')}>
          <Ticket size={16} /> {t('coupons.tabCoupons')}
        </button>
        <button type="button" className={!isCoupons ? styles.tabActive : styles.tab} onClick={() => setTab('campaigns')}>
          <Megaphone size={16} /> {t('campaigns.tabCampaigns')}
        </button>
      </div>

      {isCoupons ? (
        <DataTable
          columns={couponColumns}
          rows={coupons}
          loading={loading}
          empty={<EmptyState icon={Ticket} title={t('coupons.emptyTitle')} description={t('coupons.emptyDescription')}
            action={<Button icon={Plus} onClick={() => setEditing({})}>{t('coupons.newCoupon')}</Button>} />}
        />
      ) : (
        <DataTable
          columns={campaignColumns}
          rows={campaigns}
          loading={loading}
          empty={<EmptyState icon={Megaphone} title={t('campaigns.emptyTitle')} description={t('campaigns.emptyDescription')}
            action={<Button icon={Plus} onClick={() => setEditing({})}>{t('campaigns.newCampaign')}</Button>} />}
        />
      )}

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        kicker={editing?.id ? t('common.edit') : t('coupons.modalKickerNew')}
        title={isCoupons ? t('coupons.modalTitle') : t('campaigns.modalTitle')}
        size="md"
      >
        {editing &&
          (isCoupons ? (
            <CouponForm initial={editing} onSubmit={saveCoupon} onCancel={() => setEditing(null)} />
          ) : (
            <CampaignForm initial={editing} onSubmit={saveCampaign} onCancel={() => setEditing(null)} />
          ))}
      </Modal>
    </>
  );
}
